import { FileUploadInfoDc } from "@evergis/api";
import { MutableRefObject, useCallback, useRef } from "react";
import { uploadFile, uploadFileUrl } from "../../../../resources/file";

export interface FileData {
  name: string;
  type: string;
  size: number;
  id?: string;
  resourceId?: string;
}

export type FileLoader = {
  input: MutableRefObject<HTMLInputElement>;
  loading: boolean;
};

interface FileLoaderActions {
  loadFile: VoidFunction;
  upload: (file: File, rewrite?: boolean) => void;
  uploadFileURL: (fileInfo: FileUploadInfoDc, url: string, rewrite?: boolean) => void;
}

export const useFileLoader = (): [FileData & FileLoader, FileLoaderActions] => {
  const [uploadMutation, { data = {}, loading }] = uploadFile.use();
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const { fileId, resourceId } = data as unknown as any;
  const [uploadFileURLMutation, { data: fileURLId, loading: loadingFromURL }] = uploadFileUrl.use();
  const input = useRef<HTMLInputElement>();
  const name = useRef<string>();
  const type = useRef<string>();
  const size = useRef<number>();

  const loadFile = useCallback(() => {
    if (input.current) {
      input.current.click();
    }
  }, [input.current]);

  const upload = useCallback(async (file: File, rewrite?: boolean) => {
    if (!file) return;

    name.current = file.name;
    type.current = file.name.split(".").pop();
    size.current = file.size;

    await uploadMutation({ input: { file, rewrite } });
  }, []);

  const uploadFileURL = useCallback(async (fileInfo: FileUploadInfoDc, url: string, rewrite?: boolean) => {
    const { fileName, contentLength, contentType } = fileInfo;

    name.current = fileName;
    type.current = contentType;
    size.current = contentLength;

    await uploadFileURLMutation({ input: { url, rewrite } });
  }, []);

  return [
    {
      input,
      id: fileId || fileURLId,
      resourceId,
      name: name.current,
      type: type.current,
      size: size.current,
      loading: loading || loadingFromURL,
    },
    {
      loadFile,
      upload,
      uploadFileURL,
    },
  ];
};
